import React, { useMemo, useState, useEffect } from 'react';
import Shapes from './Shapes';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Badge from 'react-bootstrap/Badge';
import Card from 'react-bootstrap/Card';
import Nav from 'react-bootstrap/Nav';
import Button from 'react-bootstrap/Button';
import Accordion from 'react-bootstrap/Accordion';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';

import reactIcon from '../icons/react.png';
import springIcon from '../icons/springBoot.svg';
import gears from '../icons/gears.svg';
import javaIcon from '../icons/java-icon.svg';
import jsIcon from '../icons/js.webp';
import postgresIcon from '../icons/postger.svg';
import restApiIcon from '../icons/restApi.png';
import gitIcon from '../icons/git.svg';
import pythonIcon from '../icons/python.svg';
import oopIcon from '../icons/oop.png';
import soapIcon from '../icons/soap.webp';
import sqlServerIcon from '../icons/sqldb.webp';
import oracleIcon from '../icons/oracleDB.webp';
import springBootIcon from '../icons/springBoot.webp';
import intellijIcon from '../icons/intj.webp';
import banking from '../icons/bank.png';
import activemq from '../icons/activmq.png';

const SkillIcon = ({ src, alt }) => (
  <img src={src} alt={alt} height={32} width={32} loading="lazy" style={{ objectFit: 'contain' }} />
);

const ALL_SKILLS = [
  { label: 'Java', key: 'java', icon: javaIcon, category: 'Backend', level: 'Advanced', years: 6, tags: ['OOP', 'JVM'] },
  { label: 'Spring Framework', key: 'spring', icon: springIcon, category: 'Backend', level: 'Advanced', years: 4, tags: ['Core', 'IOC'] },
  { label: 'Spring Boot', key: 'springboot', icon: springBootIcon, category: 'Backend', level: 'Advanced', years: 3.5, tags: ['REST', 'Microservices'] },
  { label: 'REST API', key: 'rest', icon: restApiIcon, category: 'Integration', level: 'Advanced', years: 3.5, tags: ['JSON', 'Swagger'] },
  { label: 'SOAP', key: 'soap', icon: soapIcon, category: 'Integration', level: 'Intermediate', years: 2, tags: ['XML', 'WSDL'] },
  { label: 'JavaScript', key: 'javascript', icon: jsIcon, category: 'Frontend', level: 'Advanced', years: 4, tags: ['ES6+', 'DOM'] },
  { label: 'React', key: 'react', icon: reactIcon, category: 'Frontend', level: 'Advanced', years: 2.5, tags: ['Hooks', 'SPA'] },
  { label: 'Python', key: 'python', icon: pythonIcon, category: 'Scripting', level: 'Intermediate', years: 2, tags: ['Automation'] },
  { label: 'Oracle DB', key: 'oracle', icon: oracleIcon, category: 'Databases', level: 'Advanced', years: 3, tags: ['PL/SQL', 'Procedures'] },
  { label: 'PostgreSQL', key: 'postgres', icon: postgresIcon, category: 'Databases', level: 'Intermediate', years: 2, tags: ['SQL', 'Functions'] },
  { label: 'SQL Server', key: 'sqlserver', icon: sqlServerIcon, category: 'Databases', level: 'Intermediate', years: 2, tags: ['T-SQL'] },
  { label: 'Apache Camel', key: 'camel', icon: gears, category: 'Integration', level: 'Intermediate', years: 1, tags: ['Routes', 'EIPs'] },
  { label: 'Spring Integration', key: 'spring-integration', icon: springIcon, category: 'Integration', level: 'Advanced', years: 2, tags: ['EIP', 'Adapters'] },
  { label: 'ActiveMQ', key: 'activemq', icon: activemq, category: 'Messaging', level: 'Advanced', years: 2, tags: ['JMS'] },
  { label: 'ISO 20022', key: 'iso20022', icon: banking, category: 'Banking', level: 'Advanced', years: 2.5, tags: ['pacs.008', 'camt.053'] },
  { label: 'SWIFT MT/MX', key: 'swift', icon: banking, category: 'Banking', level: 'Advanced', years: 2.5, tags: ['MT103', 'MX'] },
  { label: 'ACH/RTGS', key: 'ach', icon: banking, category: 'Banking', level: 'Advanced', years: 2, tags: ['Clearing', 'Settlement'] },
  { label: 'iMAL DFC/DFS', key: 'imal', icon: banking, category: 'Banking', level: 'Advanced', years: 2.5, tags: ['File-Based', 'Integration'] },
  { label: 'Digital Hub', key: 'digital-hub', icon: banking, category: 'Banking', level: 'Advanced', years: 2, tags: ['Routing', 'MX Logic'] },
  { label: 'Alerts', key: 'alerts', icon: banking, category: 'Banking', level: 'Advanced', years: 2.5, tags: ['Emails', 'SMS'] },
  { label: 'Reporting', key: 'reporting', icon: banking, category: 'Banking', level: 'Advanced', years: 2.5, tags: ['JasperSoft', 'DJ Converter'] },
  { label: 'Git', key: 'git', icon: gitIcon, category: 'Tools', level: 'Advanced', years: 3, tags: ['Branching', 'CI/CD'] },
  { label: 'IntelliJ IDEA', key: 'intellij', icon: intellijIcon, category: 'Tools', level: 'Advanced', years: 4, tags: ['Productivity'] },
  { label: 'Postman', key: 'postman', icon: gears, category: 'Tools', level: 'Advanced', years: 3, tags: ['API Testing'] },
  { label: 'Maven', key: 'maven', icon: gears, category: 'Tools', level: 'Advanced', years: 3, tags: ['Builds'] },
  { label: 'Jenkins', key: 'jenkins', icon: gears, category: 'DevOps', level: 'Intermediate', years: 1, tags: ['Automation'] },
  { label: 'Docker', key: 'docker', icon: gears, category: 'DevOps', level: 'Intermediate', years: 1, tags: ['Containers'] },
  { label: 'Linux Shell', key: 'linux', icon: gears, category: 'Tools', level: 'Intermediate', years: 2, tags: ['Scripting'] },
  { label: 'OOP', key: 'oop', icon: oopIcon, category: 'Concepts', level: 'Advanced', years: 5, tags: ['SOLID'] },
  { label: 'Microservices', key: 'microservices', icon: gears, category: 'Concepts', level: 'Intermediate', years: 2, tags: ['Distributed Systems'] },
  { label: 'Design Patterns', key: 'patterns', icon: gears, category: 'Concepts', level: 'Advanced', years: 3, tags: ['Singleton', 'Factory'] },
  { label: 'API Security (OAuth 2.0)', key: 'oauth', icon: gears, category: 'Security', level: 'Intermediate', years: 2, tags: ['JWT', 'PKCE'] },
  { label: 'TLS/mTLS', key: 'tls', icon: gears, category: 'Security', level: 'Intermediate', years: 2, tags: ['Certificates'] }
];

const CATEGORIES = ['All', 'Frontend', 'Backend', 'Databases', 'Integration', 'Messaging', 'Banking', 'Tools', 'DevOps', 'Concepts', 'Security', 'Scripting'];
const CATEGORY_ORDER = CATEGORIES.filter(c => c !== 'All');

const SkillCard = ({ skill }) => (
  <OverlayTrigger placement="top" overlay={<Tooltip>{`${skill.level} • ${skill.years} yrs`}</Tooltip>}>
    <Card className="h-100 border-0 shadow-sm skill-card" role="listitem">
      <Card.Body className="d-flex align-items-center gap-3 p-3">
        <SkillIcon src={skill.icon} alt={`${skill.label} icon`} />
        <div className="flex-grow-1">
          <div className="fw-semibold d-flex align-items-center justify-content-between flex-wrap">
            <span>{skill.label}</span>
            {skill.level && <Badge bg="secondary" pill>{skill.level}</Badge>}
          </div>
          <small className="text-muted d-block mt-1">
            {skill.tags?.slice(0, 2).join(', ')}
          </small>
        </div>
      </Card.Body>
    </Card>
  </OverlayTrigger>
);

const useDebounced = (value, delay = 180) => {
  const [v, setV] = useState(value);
  useEffect(() => { const id = setTimeout(() => setV(value), delay); return () => clearTimeout(id); }, [value, delay]);
  return v;
};

const SkillList = () => {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('All');
  const debouncedQuery = useDebounced(query);
  const [activeKeys, setActiveKeys] = useState(CATEGORY_ORDER);

  const filtered = useMemo(() => {
    const q = debouncedQuery.trim().toLowerCase();
    return ALL_SKILLS.filter((s) =>
      (category === 'All' || s.category === category) &&
      (!q || s.label.toLowerCase().includes(q) || s.tags?.some(t => t.toLowerCase().includes(q)))
    );
  }, [debouncedQuery, category]);

  const grouped = useMemo(() => {
    const map = Object.create(null);
    CATEGORY_ORDER.forEach(c => (map[c] = []));
    for (const s of filtered) {
      if (!map[s.category]) map[s.category] = [];
      map[s.category].push(s);
    }
    for (const c of Object.keys(map)) {
      map[c].sort((a, b) => a.label.localeCompare(b.label));
    }
    return map;
  }, [filtered]);

  const totalShown = filtered.length;

  // Expand/Collapse all handlers
  const collapseAll = () => setActiveKeys([]);
  const expandAll = () => setActiveKeys(CATEGORY_ORDER);
  const allCollapsed = activeKeys.length === 0;

  return (
    <div className="hero-section" aria-labelledby="skills-heading">
      <Shapes />
      <Container className="py-4">
        <div className="d-flex align-items-center gap-2 mb-4 flex-wrap justify-content-between">
          <div className="d-flex align-items-center gap-2">
            <img height={48} width={48} src={gears} alt="Skills" />
            <h1 id="skills-heading" className="m-0">Professional Skills</h1>
          </div>
          <small className="text-muted">Integration & Core Banking Expertise</small>
        </div>

        <Row className="g-3 align-items-end mb-3" role="group">
          <Col xs={12} md={5}>
            <Form.Control type="search" placeholder="Search a skill or tag…" value={query} onChange={(e) => setQuery(e.target.value)} />
          </Col>
          <Col xs={12} md={7}>
            <Nav variant="pills" activeKey={category} className="flex-nowrap overflow-auto no-scrollbar" onSelect={(k) => setCategory(k || 'All')}>
              {CATEGORIES.map((c) => (
                <Nav.Item key={c}><Nav.Link eventKey={c}>{c}</Nav.Link></Nav.Item>
              ))}
            </Nav>
          </Col>
        </Row>

        <section className="pt-3" aria-live="polite">
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <small className="text-muted">{totalShown} skill{totalShown !== 1 ? 's' : ''} shown</small>
            <Button size="sm" variant="outline-primary" onClick={() => (allCollapsed ? expandAll() : collapseAll())}>
              {allCollapsed ? 'Expand all' : 'Collapse all'}
            </Button>
          </div>

          {CATEGORY_ORDER.map(cat => {
            const items = grouped[cat] || [];
            if (items.length === 0) return null;
            const isOpen = activeKeys.includes(cat);
            return (
              <Accordion key={cat} activeKey={isOpen ? cat : undefined} onSelect={() => {
                setActiveKeys(prev => prev.includes(cat) ? prev.filter(x => x !== cat) : [...prev, cat]);
              }}>
                <Accordion.Item eventKey={cat} className="mb-3 border-0 shadow-sm rounded-3">
                  <Accordion.Header>
                    <div className="d-flex align-items-center gap-3">
                      <img src={cat === 'Banking' ? banking : gears} alt="cat icon" height={26} width={26} />
                      <span className="fw-semibold fs-6 text-primary">{cat}</span>
                      <Badge bg="light" text="dark">{items.length}</Badge>
                    </div>
                  </Accordion.Header>
                  <Accordion.Body>
                    <div className="skills-grid" role="list">
                      {items.map(skill => (<SkillCard key={skill.key} skill={skill} />))}
                    </div>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            );
          })}
        </section>

        <style>{`
          .skills-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 18px; }
          .skill-card { border-radius: 16px; transition: all .2s ease; background: #fff; }
          .skill-card:hover { transform: translateY(-3px); box-shadow: 0 .5rem 1.5rem rgba(0,0,0,.1); }
          .no-scrollbar::-webkit-scrollbar { display: none; }
          .accordion-button:not(.collapsed) { background: #f5f8ff; color: #0d6efd; }
          .accordion-item { border-radius: 14px !important; overflow: hidden; }
          h1 { font-weight: 600; letter-spacing: .5px; }
        `}</style>
      </Container>
    </div>
  );
};

export default SkillList;
